import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { OrdersApiAdapter } from '../adapters/OrdersApiAdapter';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import type { Order, OrderLine } from '../models/Order';

export const useOrder = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const isInitialLoadRef = useRef<boolean>(true);

  const fetchOrder = useCallback(async (showLoader = true) => {
    if (!orderId) {
      setError('No se ha proporcionado un ID de pedido.');
      setLoading(false);
      return;
    }

    const shouldShowLoader = showLoader && isInitialLoadRef.current;

    try {
      // Solo mostrar el loader en la carga inicial
      if (shouldShowLoader) {
        setLoading(true);
      }
      
      const api = new OrdersApiAdapter(createHttpClient(apiClientWH));
      const orderData = await api.getOrderById(Number(orderId));
      setOrder(orderData);
      setError(null);
      
      // Marcar que la carga inicial ya se completó
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
      }
    } catch (err: any) {
      setError(err.message || 'Error al cargar el pedido');
    } finally {
      // Solo cambiar loading a false si lo habíamos activado
      if (shouldShowLoader) {
        setLoading(false);
      }
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrder(true);
  }, [fetchOrder]);

  // Función de refetch que actualiza sin mostrar spinner
  const refetch = useCallback(() => {
    fetchOrder(false);
  }, [fetchOrder]);

  return {
    order,
    loading,
    error,
    refetch,
  };
};
